import type { FeedbackEventProps, FeedbackStore } from './interfaces'
import type { SurveyAnswer } from '@brighterly/lib-core-types/survey/interfaces'

import { Discipline } from '@brighterly/lib-core-types/discipline/enums'

import { useKidRoutes } from '@entities/customer'
import { toAnalyticsId } from '@shared/lib/amplitude'

import { ExperienceIssues } from '../enums'
import {
  listFeedbackRoles,
  listFeedbackCategories,
  listExperienceIssues,
  listConnectionIssues,
  listAdditionalPractice,
} from '../../config/reasons'
import { AdditionalPractice, FeedbackCategory, FeedbackRole, FeedbackStep, FeedbackSurveyType } from './enums'

const isFirstLessonFlow = (state: FeedbackStore): boolean =>
  state.surveyType === FeedbackSurveyType.AfterFirstLessonFeedback

const isFirstLessonWithNewTutorFlow = (state: FeedbackStore): boolean =>
  state.surveyType === FeedbackSurveyType.AfterFirstLessonWithNewTutorFeedback

const isLessonFeedbackFlow = (state: FeedbackStore): boolean =>
  isFirstLessonFlow(state) || isFirstLessonWithNewTutorFlow(state)

const isDetailedFeedbackRequired = (state: FeedbackStore): boolean => {
  if (!isLessonFeedbackFlow(state) || state.role !== FeedbackRole.Parent) {
    return false
  }

  const isLessonRatingLow = state.rating !== null && state.rating < 3
  const isClassroomRatingLow = state.classroomRating !== null && state.classroomRating < 3
  const isCommunicationRatingLow = state.tutorTransitionRating !== null && state.tutorTransitionRating < 3

  return isLessonRatingLow || isClassroomRatingLow || isCommunicationRatingLow
}

const isDetailedFeedbackRequirementMet = (state: FeedbackStore): boolean =>
  !isDetailedFeedbackRequired(state) || state.detailedFeedback.trim().length > 0

export const getStepOrder = (state: FeedbackStore): FeedbackStep[] => {
  if (isLessonFeedbackFlow(state)) {
    const lessonSteps = [
      FeedbackStep.Role,
      FeedbackStep.Rating,
    ]

    if (isFirstLessonFlow(state)) {
      lessonSteps.push(FeedbackStep.ClassroomRating)
    }

    lessonSteps.push(FeedbackStep.TutorTransition)

    if (state.role === FeedbackRole.Parent) {
      lessonSteps.push(FeedbackStep.DetailedFeedback)
    }

    lessonSteps.push(FeedbackStep.Done)

    return lessonSteps
  }

  const steps = [
    FeedbackStep.Role,
    FeedbackStep.Rating,
  ]

  const isAdditionalPractice = state.role === FeedbackRole.Parent
    && hasAdditionalPracticeOption(state)

  if (isAdditionalPractice) {
    steps.push(FeedbackStep.AdditionalPractice)
  }

  steps.push(FeedbackStep.Done)

  return steps
}

const getBooking = (state: FeedbackStore): FeedbackStore['booking'] => state.booking

const getFeedbackUserRole = (role: FeedbackRole | null): FeedbackEventProps['user_role'] => {
  switch (role) {
    case FeedbackRole.Parent:
      return 'customer'
    case FeedbackRole.Student:
      return 'kid'
    default:
      return null
  }
}
const getDiscipline = (state: FeedbackStore): FeedbackStore['discipline'] => state.discipline

const hasAdditionalPracticeOption = (state: FeedbackStore): boolean => state.options?.additional_practice || false
const isFeedbackSubmitted = (state: FeedbackStore): boolean => state.options?.is_submitted || false

const getFeedbackAnswers = (state: FeedbackStore): SurveyAnswer[] => {
  if (isLessonFeedbackFlow(state)) {
    return getLessonFeedbackAnswers(state)
  }

  return getStandardFeedbackAnswers(state)
}

const getStandardFeedbackAnswers = (state: FeedbackStore): SurveyAnswer[] => {
  const answers: SurveyAnswer[] = []
  const isNegative = state.rating !== null && state.rating <= 3

  if (state.role) {
    answers.push({ question: 'role', answer: listFeedbackRoles[state.role] })
  }

  if (state.rating !== null) {
    answers.push({ question: 'rating', answer: state.rating })
  }

  if (isNegative && state.category) {
    answers.push({ question: 'category', answer: listFeedbackCategories[state.category] })
  }

  if (isNegative && state.selectedIssues.length > 0) {
    const issueLabels = state.category === FeedbackCategory.Connection
      ? listConnectionIssues
      : listExperienceIssues

    const labels = state.selectedIssues.map(issue => issueLabels[issue as keyof typeof issueLabels])
    answers.push({ question: 'issues', answer: labels.join(', '), comment: state.comment.trim() || undefined })
  }

  if (state.additionalPractice) {
    answers.push({ question: 'additional_practice', answer: listAdditionalPractice[state.additionalPractice] })
  }

  return answers
}

const getLessonFeedbackAnswers = (state: FeedbackStore): SurveyAnswer[] => {
  const answers: SurveyAnswer[] = []

  if (state.role) {
    answers.push({ question: 'role', answer: listFeedbackRoles[state.role] })
  }

  if (state.rating !== null) {
    answers.push({ question: 'rating', answer: state.rating })
  }

  if (state.classroomRating !== null) {
    answers.push({ question: 'classroom_rating', answer: state.classroomRating })
  }

  if (state.tutorTransitionRating !== null) {
    answers.push({ question: 'tutor_transition_rating', answer: state.tutorTransitionRating })
  }

  if (state.role === FeedbackRole.Parent) {
    const trimmed = state.detailedFeedback.trim()
    if (trimmed) {
      answers.push({ question: 'detailed_feedback', answer: trimmed })
    }
  }

  return answers
}

export const getters = {
  isReady: (state: FeedbackStore): FeedbackStore['ready'] => state.ready,
  getBooking: (state: FeedbackStore): FeedbackStore['booking'] => getBooking(state),
  getDiscipline: (state: FeedbackStore): FeedbackStore['discipline'] => getDiscipline(state),
  getOptions: (state: FeedbackStore): FeedbackStore['options'] => state.options,
  isFeedbackSubmitted: (state: FeedbackStore): boolean => isFeedbackSubmitted(state),
  isOptions: (state: FeedbackStore): boolean => !!state.options,
  isDiscipline: (state: FeedbackStore): boolean => !!state.discipline,
  getRedirectUrl: (state: FeedbackStore): string | null => {
    const {
      getHomeMath,
      getHomeEla,
    } = useKidRoutes()

    const ela = state.discipline?.code === Discipline.ELA
    const math = state.discipline?.code === Discipline.Math
    const kidId = state.discipline?.kid?.id || null

    if (kidId && (ela || math)) {
      return ela
        ? getHomeEla(kidId)
        : getHomeMath(kidId)
    } else {
      return null
    }
  },
  isBooking: (state: FeedbackStore): boolean => !!state.booking,
  getStepOrder: (state: FeedbackStore): FeedbackStep[] => getStepOrder(state),
  getStep: (state: FeedbackStore): FeedbackStep => state.step,

  getSurveyType: (state: FeedbackStore): FeedbackSurveyType => state.surveyType,
  isFirstLessonFlow: (state: FeedbackStore): boolean => isFirstLessonFlow(state),
  isFirstLessonWithNewTutorFlow: (state: FeedbackStore): boolean => isFirstLessonWithNewTutorFlow(state),
  isLessonFeedbackFlow: (state: FeedbackStore): boolean => isLessonFeedbackFlow(state),

  isLastStep: (state: FeedbackStore): boolean => {
    const stepOrder = getStepOrder(state)
    const currentIndex = stepOrder.indexOf(state.step)
    if (currentIndex < 0) return false
    return currentIndex === stepOrder.length - 2
  },

  getRole: (state: FeedbackStore): FeedbackStore['role'] => state.role,

  getRating: (state: FeedbackStore): FeedbackStore['rating'] => state.rating,

  getClassroomRating: (state: FeedbackStore): FeedbackStore['classroomRating'] => state.classroomRating,

  getTutorTransitionRating: (state: FeedbackStore): FeedbackStore['tutorTransitionRating'] => state.tutorTransitionRating,

  getDetailedFeedback: (state: FeedbackStore): FeedbackStore['detailedFeedback'] => state.detailedFeedback,

  isDetailedFeedbackRequired: (state: FeedbackStore): boolean => isDetailedFeedbackRequired(state),

  getCategory: (state: FeedbackStore): FeedbackStore['category'] => state.category,

  isCategory: (state: FeedbackStore): boolean => !!state.category,

  getSelectedIssues: (state: FeedbackStore): FeedbackStore['selectedIssues'] => state.selectedIssues,

  getComment: (state: FeedbackStore): FeedbackStore['comment'] => state.comment,

  getAdditionalPractice: (state: FeedbackStore): FeedbackStore['additionalPractice'] => state.additionalPractice,

  hasAdditionalPracticeOption: (state: FeedbackStore): boolean => hasAdditionalPracticeOption(state),

  isAdditionalPracticeAvailable: (state: FeedbackStore): boolean => {
    return state.role === FeedbackRole.Parent
      && hasAdditionalPracticeOption(state)
  },

  isStepRatingWithAdditionalPracticeAhead: (state: FeedbackStore): boolean => {
    return state.step === FeedbackStep.Rating
      && state.role === FeedbackRole.Parent
      && hasAdditionalPracticeOption(state)
  },

  hasAdditionalPracticeInteraction: (state: FeedbackStore): boolean => {
    return state.additionalPractice !== null
  },

  isAdditionalPracticeRequested: (state: FeedbackStore): boolean => {
    return state.additionalPractice === AdditionalPractice.Yes
  },

  isPositiveRating: (state: FeedbackStore): boolean => {
    return state.rating !== null && state.rating >= 4
  },

  isReferralDone: (state: FeedbackStore): boolean => {
    return state.role === FeedbackRole.Parent && state.rating !== null && state.rating >= 4
  },

  isNegativeRating: (state: FeedbackStore): boolean => {
    return state.rating !== null && state.rating <= 3
  },

  isCommentRequired: (state: FeedbackStore): boolean => {
    return state.category === FeedbackCategory.LearningExperience
      && state.selectedIssues.includes(ExperienceIssues.Other)
  },

  isCommentRequiredAndEmpty: (state: FeedbackStore): boolean => {
    return state.category === FeedbackCategory.LearningExperience
      && state.selectedIssues.includes(ExperienceIssues.Other)
      && !state.comment.trim()
  },

  canSendFeedback: (state: FeedbackStore): boolean => {
    switch (state.step) {
      case FeedbackStep.Rating: {
        if (state.rating === null) return false
        if (isLessonFeedbackFlow(state)) return state.rating > 0
        if (state.rating >= 4) return true
        return state.selectedIssues.length > 0
      }
      case FeedbackStep.ClassroomRating:
        return state.classroomRating !== null && state.classroomRating > 0
      case FeedbackStep.TutorTransition:
        return state.tutorTransitionRating !== null && state.tutorTransitionRating > 0
      case FeedbackStep.DetailedFeedback:
        return isDetailedFeedbackRequirementMet(state)
      case FeedbackStep.AdditionalPractice:
        return state.additionalPractice !== null
      default:
        return false
    }
  },

  isRatingStepValid: (state: FeedbackStore): boolean => {
    if (state.rating === null) return false

    if (state.rating >= 4) return true

    if (state.category === null) return false
    if (state.selectedIssues.length === 0) return false

    const hasOther = state.selectedIssues.includes(ExperienceIssues.Other)
    if (hasOther && !state.comment.trim()) return false

    return true
  },

  canGoBack: (state: FeedbackStore): boolean => {
    const stepOrder = getStepOrder(state)
    const currentIndex = stepOrder.indexOf(state.step)
    if (currentIndex <= 0) return false
    return state.step !== FeedbackStep.Done
  },

  isStepValid: (state: FeedbackStore): boolean => {
    switch (state.step) {
      case FeedbackStep.Role:
        return state.role !== null
      case FeedbackStep.Rating:
        if (state.rating === null) return false
        if (isLessonFeedbackFlow(state)) return state.rating > 0
        return state.rating >= 4
          || (state.category !== null
            && state.selectedIssues.length > 0
            && !(state.selectedIssues.includes(ExperienceIssues.Other) && !state.comment.trim()))
      case FeedbackStep.ClassroomRating:
        return state.classroomRating !== null && state.classroomRating > 0
      case FeedbackStep.TutorTransition:
        return state.tutorTransitionRating !== null && state.tutorTransitionRating > 0
      case FeedbackStep.DetailedFeedback:
        return isDetailedFeedbackRequirementMet(state)
      case FeedbackStep.Category:
        return state.category !== null
      case FeedbackStep.Issues:
        return state.selectedIssues.length > 0
      case FeedbackStep.Comment:
        return true
      case FeedbackStep.AdditionalPractice:
        return state.additionalPractice !== null
      case FeedbackStep.Done:
        return true
      default:
        return false
    }
  },

  isDone: (state: FeedbackStore): boolean => state.step === FeedbackStep.Done,

  getFeedbackAnswers: (state: FeedbackStore): SurveyAnswer[] => getFeedbackAnswers(state),
  getEventProps: (state: FeedbackStore): FeedbackEventProps => {
    const booking = getBooking(state)

    return {
      booking_id: booking?.id ?? null,
      booking_type: booking?.type ? toAnalyticsId(booking.type) : null,
      booking_status: booking?.status ? toAnalyticsId(booking.status) : null,
      item_type: 'survey',
      item_category: toAnalyticsId(state.surveyType),
      user_role: getFeedbackUserRole(state.role),
      // The lesson's discipline: the page sits outside kid routes, so the active one may differ (spec D8).
      discipline_id: state.discipline?.id ?? null,
      discipline_code: state.discipline?.code ? toAnalyticsId(state.discipline.code) : null,
    }
  },
}
